import { useIsNangoIntegrationsEnabled } from '@/features/flags';
import { ApprovalInputDetails } from '@/features/projects/components/approval-input-details';
import { useTemplateCreationApprovalGuard } from '@/features/templateBuilder/hooks/forms/useTemplateCreationApprovalGuard';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import type { ApprovalRequestBlockProps, ApprovalRequestInput } from '@heidi/agents';
import { ApprovalRequestBlock, useMutateResolveApproval } from '@heidi/agents';
import type { ProtectedActionChildRunApproval } from '@heidi/agents/contracts';
import { useQueryPatientProfileById } from '@heidi/patients/data';
import { useCallback } from 'react';

import { desktopControlApprovalPort } from './desktop-control-approval';

// The main window's approval surface for the template quality attestation.
const useAskHeidiApprovalGuard = () =>
  useTemplateCreationApprovalGuard('ask_heidi_approval');

const renderInputDetails = (approval: ApprovalRequestInput) => (
  <ApprovalInputDetails approval={approval} />
);

// The patient-context card's reads: the app's auth verdict and the patients door.
const patientContext: ApprovalRequestBlockProps['patientContext'] = {
  useAuth: useIsAuthenticated,
  useProfile: useQueryPatientProfileById,
};

/**
 * Resolves against the child run: the parent chat's resolver is parent-scoped.
 * Throwing on failure lets the card unlatch and show its own toast.
 */
export const ProtectedActionApproval = ({
  approval,
}: {
  approval: ProtectedActionChildRunApproval;
}) => {
  const { mutateAsync } = useMutateResolveApproval();
  const isNangoIntegrationsEnabled = useIsNangoIntegrationsEnabled();
  const { agentRunId } = approval;

  const onResolve = useCallback<ApprovalRequestBlockProps['onResolve']>(
    async (approvalId, verdict, reason) => {
      const response = await mutateAsync({
        agentRunId,
        approvalId,
        body: { verdict, reason, modified_input: null },
      });
      if (!response.accepted) throw new Error('Approval was not accepted');
    },
    [agentRunId, mutateAsync]
  );

  return (
    <ApprovalRequestBlock
      agentRunId={agentRunId}
      approval={{
        approval_id: approval.approvalId,
        tool_name: approval.toolName,
        tool_input: approval.toolInput,
        tool_call_id: approval.toolCallId,
        toolCallDisplay: approval.toolCallDisplay,
        context: approval.context,
      }}
      onResolve={onResolve}
      isNangoIntegrationsEnabled={isNangoIntegrationsEnabled}
      desktopControl={desktopControlApprovalPort}
      useApprovalGuard={useAskHeidiApprovalGuard}
      renderInputDetails={renderInputDetails}
      patientContext={patientContext}
    />
  );
};
